import React, {
  useState,
  useMemo,
  useImperativeHandle,
  forwardRef,
} from "react";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import DataModal from "./DataModal";
import HostTooltipButton from "./HostTooltipButton";
import { useChartsStore } from "~/store/chartStore";
import IndicatorEditModal from "./IndicatorEditModal";
import indicatorRegistry from "~/Registry/Indicators/IndicatorRegistry";
import { getBaseDisplayName } from "~/utils/indicatorDisplayName";
import { getSourcePathLabel } from "@chartLib/lib/utils/getSourcePath";
import getChartHostBody from "~/utils/chartHostBody";

const ActiveIndicatorsModal = forwardRef((props, ref) => {
  const [visible, setVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [selectedIndicator, setSelectedIndicator] = useState(null);
  // Janela onde o gráfico alvo vive — resolvida no clique, nunca no render (#375).
  const [editModalHostBody, setEditModalHostBody] = useState(null);
  const [highlightedIndicatorId, setHighlightedIndicatorId] = useState(null);

  // Chart store selectors
  const selectedChart = useChartsStore((state) => state.getSelectedChart());
  const selectedChartId = selectedChart?.id || null;
  const indicators = selectedChart?.indicators || [];
  const removeIndicatorFromChart = useChartsStore(
    (state) => state.removeIndicatorFromChart,
  );
  const updateIndicatorConfig = useChartsStore(
    (state) => state.updateIndicatorConfig,
  );

  // Get the registry entry for the selected indicator
  const registryEntry = useMemo(() => {
    if (!selectedIndicator || !selectedIndicator.key) {
      return null;
    }
    for (const categoryKey of Object.keys(indicatorRegistry)) {
      const entry =
        indicatorRegistry[categoryKey].indicators[selectedIndicator.key];
      if (entry) return entry;
    }
    return null;
  }, [selectedIndicator]);

  // Merge registry entry with actual indicator config for editing
  const indicatorEntryForEdit = useMemo(() => {
    if (!registryEntry || !selectedIndicator) {
      return registryEntry;
    }
    return {
      ...registryEntry,
      config: selectedIndicator.config || {},
      appearanceConfig: selectedIndicator.appearanceConfig || {},
    };
  }, [registryEntry, selectedIndicator]);

  // Helper to find category from registry
  const getCategoryDisplayName = (indicatorKey) => {
    for (const categoryKey of Object.keys(indicatorRegistry)) {
      const category = indicatorRegistry[categoryKey];
      if (category.indicators && category.indicators[indicatorKey]) {
        return category.displayName || categoryKey;
      }
    }
    return "N/A";
  };

  // Memoized indicator data for table
  const indicatorTableData = useMemo(() => {
    return indicators.map((indicator) => {
      // Regenerate display name to ensure consistency
      // This handles old indicators that might have inconsistent naming
      const displayName = getBaseDisplayName(indicator);

      // Get category from indicator or look up in registry
      const category =
        indicator.categoryDisplayName ||
        indicator.category ||
        getCategoryDisplayName(indicator.key);

      // Get source path label (pass indicators for resolving indicator sources)
      const sourcePath = indicator.config?.sourcePath;
      const source = getSourcePathLabel(sourcePath, indicators);

      return {
        id: indicator.id,
        name: displayName,
        category,
        source,
        description: indicator.description || "",
        config: indicator.config || {},
        appearanceConfig: indicator.appearanceConfig || {},
        fullIndicator: indicator,
      };
    });
  }, [indicators]);

  const handleEditIndicator = (indicator) => {
    setSelectedIndicator(indicator.fullIndicator);
    setEditModalHostBody(getChartHostBody(selectedChartId));
    setEditModalVisible(true);
  };

  const handleDeleteIndicator = (indicator) => {
    if (selectedChartId) {
      removeIndicatorFromChart(selectedChartId, indicator.id);
    }
  };

  const handleDeleteAllIndicators = () => {
    if (!selectedChartId || !removeIndicatorFromChart) return;

    // Delete all indicators
    indicators.forEach((indicator) => {
      removeIndicatorFromChart(selectedChartId, indicator.id);
    });

    console.log("Deleted all indicators");
  };

  const handleEditModalSave = (options, appearanceOptions) => {
    if (!selectedChartId || !selectedIndicator) return;

    updateIndicatorConfig(
      selectedChartId,
      selectedIndicator.id,
      options,
      appearanceOptions,
    );

    setEditModalVisible(false);
    setSelectedIndicator(null);
  };

  const handleEditModalCancel = () => {
    setEditModalVisible(false);
    setSelectedIndicator(null);
  };

  // Expose imperative API via ref
  useImperativeHandle(ref, () => ({
    // Open modal and optionally highlight/edit a specific indicator
    open: (indicatorIdToHighlight) => {
      setVisible(true);
      if (indicatorIdToHighlight) {
        setHighlightedIndicatorId(indicatorIdToHighlight);
        // Auto-scroll to highlighted row after modal opens
        setTimeout(() => {
          const highlightedRow = document.querySelector(
            `[data-indicator-id="${indicatorIdToHighlight}"]`,
          );
          if (highlightedRow) {
            highlightedRow.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
        }, 300);
      }
    },
    // Open modal and immediately edit a specific indicator
    edit: (indicator) => {
      setVisible(true);
      setSelectedIndicator(indicator);
      setEditModalVisible(true);
    },
    // Open modal to delete mode (just open with highlight)
    remove: (indicator) => {
      setVisible(true);
      setHighlightedIndicatorId(indicator.id);
      setTimeout(() => {
        const highlightedRow = document.querySelector(
          `[data-indicator-id="${indicator.id}"]`,
        );
        if (highlightedRow) {
          highlightedRow.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }, 300);
    },
  }));

  // Column templates
  const nameTemplate = (rowData) => {
    const isHighlighted = rowData.id === highlightedIndicatorId;
    return (
      <div
        data-indicator-id={rowData.id}
        style={{
          fontSize: "12px",
          fontWeight: "500",
          color: "var(--text-color)",
          padding: isHighlighted ? "2px 6px" : "0",
          margin: isHighlighted ? "-2px -6px" : "0",
          backgroundColor: isHighlighted
            ? "rgba(33, 150, 243, 0.2)"
            : "transparent",
          border: isHighlighted ? "2px solid var(--blue-500)" : "none",
          borderRadius: isHighlighted ? "4px" : "0",
          transition: "all 0.3s ease",
        }}
      >
        {rowData.name}
      </div>
    );
  };

  const categoryTemplate = (rowData) => {
    return (
      <span
        style={{
          fontSize: "11px",
          color: "var(--text-color-secondary)",
          backgroundColor: "rgba(0,0,0,0.05)",
          padding: "1px 5px",
          borderRadius: "8px",
          fontWeight: "500",
        }}
      >
        {rowData.category}
      </span>
    );
  };

  const sourceTemplate = (rowData) => {
    return (
      <span
        style={{
          fontSize: "11px",
          color: "var(--text-color-secondary)",
        }}
      >
        {rowData.source}
      </span>
    );
  };

  const actionsTemplate = (rowData) => {
    return (
      <div style={{ display: "flex", gap: "1px", justifyContent: "center" }}>
        <HostTooltipButton
          icon="pi pi-pencil"
          className="p-button-text p-button-sm"
          onClick={() => handleEditIndicator(rowData)}
          tooltip="Editar"
          tooltipOptions={{
            position: "top",
          }}
          style={{ fontSize: "11px", padding: "4px" }}
          data-evo-id="activeindicatorsmodal-btn-edit"
        />
        <HostTooltipButton
          icon="pi pi-trash"
          className="p-button-text p-button-sm p-button-danger"
          onClick={() => handleDeleteIndicator(rowData)}
          tooltip="Remover"
          tooltipOptions={{
            position: "top",
          }}
          style={{ fontSize: "11px", padding: "4px" }}
          data-evo-id="activeindicatorsmodal-btn-delete"
        />
      </div>
    );
  };

  const headerTemplate = () => {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <span style={{ fontSize: "0.875rem", fontWeight: "600" }}>
            Estudos Ativos
          </span>
          <span
            style={{
              fontSize: "11px",
              color: "var(--text-color-secondary)",
              backgroundColor: "rgba(0,0,0,0.05)",
              padding: "1px 5px",
              borderRadius: "8px",
              fontWeight: "500",
            }}
          >
            {`${indicators.length} estudo${indicators.length !== 1 ? "s" : ""}`}
          </span>
        </div>
        {indicators.length > 0 && (
          <HostTooltipButton
            label="Excluir Todos"
            icon="pi pi-trash"
            className="p-button-sm p-button-danger p-button-outlined"
            onClick={handleDeleteAllIndicators}
            tooltip="Excluir todos os estudos do gráfico"
            tooltipOptions={{
              position: "left",
            }}
            style={{ fontSize: "11px", padding: "4px 8px" }}
            data-evo-id="activeindicatorsmodal-btn-delete-all"
          />
        )}
      </div>
    );
  };

  return (
    <>
      <div
        style={{
          position: "relative",
          display: "inline-block",
          marginRight: indicators.length > 0 ? "10px" : "0",
        }}
      >
        <Button
          icon="pi pi-list"
          className="navbar-button"
          label=" "
          onClick={() => setVisible(true)}
          tooltip="Ver estudos ativos"
          tooltipOptions={{ position: "bottom" }}
          disabled={!selectedChartId}
          data-evo-id="activeindicatorsmodal-btn-view-active"
        />
        {indicators.length > 0 && (
          <span
            onClick={() => setVisible(true)}
            style={{
              position: "absolute",
              top: "50%",
              right: "-10px",
              transform: "translateY(-50%)",
              background: "var(--blue-500)",
              color: "white",
              borderRadius: "50%",
              width: "18px",
              height: "18px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "10px",
              fontWeight: "bold",
              border: "2px solid var(--surface-0)",
              zIndex: 10,
              minWidth: "18px",
              boxSizing: "border-box",
              cursor: "pointer",
            }}
            data-evo-id="activeindicatorsmodal-indicator-count"
          >
            {indicators.length}
          </span>
        )}
      </div>

      <DataModal
        title={headerTemplate}
        visible={visible}
        onHide={() => setVisible(false)}
        size="large"
        maximizable={false}
        style={{ width: "min(700px, 92vw)" }}
        contentStyle={{ padding: 0, overflow: "hidden" }}
        className="active-items-modal"
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            maxHeight: "min(420px, 70vh)",
          }}
        >
          {indicators.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: "2rem",
                color: "var(--text-color-secondary)",
              }}
            >
              <i
                className="pi pi-info-circle"
                style={{ fontSize: "2rem", marginBottom: "1rem" }}
              ></i>
              <p>Nenhum estudo foi adicionado a este gráfico.</p>
              <p style={{ fontSize: "0.85rem" }}>
                Use o menu &ldquo;Estudos&rdquo; para adicionar estudos.
              </p>
            </div>
          ) : (
            <DataTable
              value={indicatorTableData}
              scrollable
              scrollHeight="flex"
              size="small"
              stripedRows
              showGridlines
              emptyMessage="Nenhum estudo encontrado"
              rowClassName={(rowData) =>
                rowData.id === highlightedIndicatorId
                  ? "highlighted-indicator-row"
                  : ""
              }
              onRowClick={(e) => {
                if (e.data.id === highlightedIndicatorId) {
                  setTimeout(() => setHighlightedIndicatorId(null), 2000);
                }
              }}
              style={{ fontSize: "0.8125rem" }}
            >
              <Column
                field="name"
                header="Nome"
                body={nameTemplate}
                sortable
                style={{ width: "35%" }}
              />
              <Column
                field="category"
                header="Categoria"
                body={categoryTemplate}
                sortable
                style={{ width: "25%" }}
              />
              <Column
                field="source"
                header="Fonte"
                body={sourceTemplate}
                sortable
                style={{ width: "20%" }}
              />
              <Column
                header="Ações"
                body={actionsTemplate}
                style={{ width: "20%", textAlign: "center" }}
              />
            </DataTable>
          )}
        </div>
      </DataModal>

      {/* Edit Modal */}
      {editModalVisible && selectedIndicator && (
        <IndicatorEditModal
          visible={editModalVisible}
          indicatorEntry={indicatorEntryForEdit}
          onSave={handleEditModalSave}
          onClose={handleEditModalCancel}
          editMode={true}
          chartIndicators={indicators}
          currentPanel={null}
          hostBody={editModalHostBody}
        />
      )}
    </>
  );
});

export default React.memo(ActiveIndicatorsModal);
